import { action, makeObservable, observable, runInAction } from 'mobx';
import type { ApplicationSdk } from '../../core/sdk';

export interface AdminApiResponse {
    status: number;
    json: {
        [key: string]: any;
    };
}

/**
 * Abstract base class for admin stores with common functionality
 */
abstract class BaseAdminStore {
    protected sdk: ApplicationSdk;

    // Common observable properties
    public isLoading: boolean = false;
    public data: any = null;
    public error: string | null = null;

    constructor(sdk: ApplicationSdk) {
        this.sdk = sdk;

        // Make base properties observable
        makeObservable(this, {
            isLoading: observable,
            data: observable,
            error: observable,
            init: action,
            fetchData: action,
            clearError: action,
        });
    }

    // Abstract methods that must be implemented by subclasses
    protected abstract getApiEndpoint(): string;
    protected abstract getApiPath(): string;
    protected buildApiParams(): any {
        return {};
    }
    protected abstract processApiResponse(response: AdminApiResponse): any;

    // Common error handling
    protected setError(error: string): void {
        this.error = error;
    }

    public clearError(): void {
        this.error = null;
    }

    // Common API fetch functionality
    public async fetchData(): Promise<void> {
        runInAction(() => {
            this.isLoading = true;
            this.error = null;
        });

        try {
            const apiParams = this.buildApiParams();
            const response: AdminApiResponse = await this.sdk.fetchApi(
                this.getApiEndpoint(),
                this.getApiPath(),
                apiParams
            );

            runInAction(() => {
                this.isLoading = false;

                if (response?.status === 200) {
                    this.data = this.processApiResponse(response);
                } else {
                    this.setError(`API Error: Status ${response?.status}`);
                }
            });
        } catch (error) {
            runInAction(() => {
                this.isLoading = false;
                this.setError(
                    `Failed to fetch data: ${error instanceof Error ? error.message : 'Unknown error'}`
                );
            });
            console.error(`Failed to fetch ${this.getApiEndpoint()} data:`, error);
        }
    }

    // Common initialization - must be implemented by subclasses but can be overridden
    public async init(): Promise<void> {
        await this.fetchData();
    }

    // Common cleanup
    public onLeaveStore(): void {
        runInAction(() => {
            this.data = null;
            this.error = null;
            this.isLoading = false;
        });
    }

    // Helper method for making additional API calls
    protected async makeApiCall(
        endpoint: string,
        path: string,
        params: any = {}
    ): Promise<AdminApiResponse> {
        try {
            return await this.sdk.fetchApi(endpoint, path, params);
        } catch (error) {
            console.error(`API call failed for ${endpoint}/${path}:`, error);
            throw error;
        }
    }

    // Common refresh functionality
    public async refresh(): Promise<void> {
        await this.fetchData();
    }
}

export default BaseAdminStore;
